import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import {
  Achievement,
  AchievementType,
  ContestRegistrationStatus,
  ContestStage,
  DEFAULT_TRADE_DURATION,
  PayoutAchievement,
  PositionInvalidReason,
  ProductType,
  TradingSystem,
  UserCoupon,
} from '@monorepo/types';

// Constant for the boundary in days (inclusive)
const DISPLAY_AS_RELATIVE_BOUNDARY = 3;

/**
 * Creates a human-readable date string for notifications.
 * - Dates within the boundary are displayed as relative times (e.g., "3 min ago", "1 hour ago", "2 days ago").
 * - Dates beyond the boundary are displayed in `DD/MM/YYYY` format.
 * @param date - The date to display
 * @returns The formatted date string
 */
const createDisplayNotificationDate = (date: string | Date): string => {
  const parsedDate = dayjs(date);
  const daysSince = dayjs().diff(parsedDate, 'day');

  // If the date is within the boundary
  if (daysSince <= DISPLAY_AS_RELATIVE_BOUNDARY) {
    const minutesSince = dayjs().diff(parsedDate, 'minutes');

    if (minutesSince < 60) {
      return `${minutesSince} min ago`;
    }

    const hoursSince = Math.floor(minutesSince / 60);
    if (hoursSince < 24) {
      return `${hoursSince} hour${hoursSince > 1 ? 's' : ''} ago`;
    }

    return `${daysSince} day${daysSince > 1 ? 's' : ''} ago`;
  }

  // If the date exceeds the boundary, show as DD/MM/YYYY
  return parsedDate.format('DD/MM/YYYY');
};

/**
 * Logs MobX observable data clearly in a readable format
 * without using `toJS`, leveraging `JSON.stringify` and `JSON.parse`.
 * Includes color formatting for better readability in the console.
 * @param data - The observable data to log
 * @param label - The label to describe the log
 * @param stringify - Option to stringify the data for output
 * @param logging - Option to disable logging
 */
const logMobxData = (
  data: unknown,
  label: string,
  stringify = false,
  logging = true,
) => {
  try {
    const serializedData = JSON.parse(JSON.stringify(data));

    const labelStyle =
      'color: white; background-color: blue; font-weight: bold; padding: 2px 4px; border-radius: 3px;';
    const dataStyle = 'color: #DDD; font-style: italic; font-size: 1.2em;';

    if (logging) {
      if (stringify) {
        console.log(
          `%c[MobX] ${label}: %c${JSON.stringify(serializedData, null, 2)}`,
          labelStyle,
          dataStyle,
        );
      } else {
        console.log(`%c[MobX] ${label}:`, labelStyle, serializedData);
      }
    }

    return serializedData;
  } catch (error) {
    const errorStyle =
      'color: white; background-color: red; font-weight: bold; padding: 2px 4px; border-radius: 3px;';
    console.error(`%c[MobX] Failed to log ${label}`, errorStyle, error);
  }
};

const getClientDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  switch (true) {
    case /ipad|tablet/.test(userAgent):
      return 'tablet';
    case /mobile|android|iphone|ipod|blackberry|iemobile|opera mini/.test(
      userAgent,
    ):
      return 'mobile';
    default:
      return 'desktop';
  }
};

/**
 *
 * @param blob
 */
function blobToBase64(blob: Blob): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result as string | null);
    reader.onerror = () => reject(reader.error);

    reader.readAsDataURL(blob);
  });
}

/**
 * Downloads a file from a blob with the specified filename
 * @param blob - The blob data to download
 * @param fileName - The name of the file to download
 */
const downloadCsv = (blob: Blob, fileName?: string) => {
  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || 'download';
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

export const getUserLocale = (): string => {
  return navigator.language || 'en-GB';
};

const getTotalPayoutImageTag = (userId: string, amount: number) => {
  return `total-payout-${amount}-${userId}`;
};

const filterAchievementsByType = (
  achievements: Achievement[],
  type: AchievementType,
) => {
  return achievements.filter(
    (achievement) => type && achievement.type === type,
  );
};

const calculateTotalPayout = (achievements: Achievement[]) => {
  return achievements
    .map(
      (achievement) => (achievement.metadata as PayoutAchievement)?.userPayout,
    )
    .reduce((acc, value) => acc + value, 0);
};

const getContestBreakpoints = ({
  contestStage,
  accountId,
  registrationStatus,
  tsAccountId,
}: {
  contestStage: ContestStage;
  accountId?: string;
  registrationStatus: ContestRegistrationStatus;
  tsAccountId?: string;
}) => {
  return {
    hasAccount: Boolean(accountId),
    isContestUpcoming: contestStage === ContestStage.Upcoming,
    isContestClosed: contestStage === ContestStage.Closed,
    isContestRunning: contestStage === ContestStage.Active,
    isRegistrationUpcoming:
      registrationStatus === ContestRegistrationStatus.Upcoming,
    isRegistrationOpen: registrationStatus === ContestRegistrationStatus.Open,
    isRegistrationEnded:
      registrationStatus === ContestRegistrationStatus.Closed,
    hasTsAccount: Boolean(tsAccountId),
  };
};

const contestPlaceSuffix = (i: number) => {
  const j = i + 1;
  switch (j) {
    case 1:
      return `${j}st place`;
    case 2:
      return `${j}nd place`;
    case 3:
      return `${j}rd place`;
    default:
      return `${j}`;
  }
};

export const getClosedPositionInvalidReasonToText = ({
  tradeDuration = DEFAULT_TRADE_DURATION,
  reason,
  t,
}: {
  reason: PositionInvalidReason;
  tradeDuration?: number;
  t?: TFunction<['position']>;
}) => {
  const text: Record<PositionInvalidReason, string> = {
    [PositionInvalidReason.Minimum60SecondsBetweenOpenAndClose]: t
      ? t('position:minimum_60_seconds_between_open_and_close')
      : 'There should be at least 60 seconds between position open and close time',
    [PositionInvalidReason.DifferenceBetweenEntryAndExit10Cents]: t
      ? t('position:difference_between_entry_and_exit_10_cents')
      : 'There should be at least 10 cents between position open and close price',
    [PositionInvalidReason.NewsTime]: t
      ? t('position:news_time')
      : 'Invalid Trade - Trades opened 2 minutes before or after high-impact news are deducted',
    [PositionInvalidReason.MinTradeDuration]: t
      ? t('position:min_trade_duration', {
          tradeDuration,
        })
      : `Trade duration is less than ${tradeDuration} seconds`,
    [PositionInvalidReason.BugInTradingSystem]: t
      ? t('position:bug_in_trading_system')
      : 'Bug in trading system',
    [PositionInvalidReason.MisprintFromTheStockExchange]: t
      ? t('position:misprint_from_the_stock_exchange')
      : 'Misprint from the stock exchange',
    [PositionInvalidReason.AfterContestEnd]: t
      ? t('position:after_contest_end')
      : 'After contest end',
    [PositionInvalidReason.UnsupportedSymbol]: t
      ? t('position:unsupported_symbol')
      : 'The symbol is not supported',
  };

  return text[reason];
};

const filterCouponsForProduct = ({
  productType,
  productId,
  coupons,
  tradingSystem,
}: {
  productType: ProductType;
  tradingSystem: TradingSystem;
  productId: string;
  coupons: UserCoupon[];
}) => {
  return coupons.filter((coupon) => {
    const item = coupon.items[0];
    if (item.productType === ProductType.General) {
      return true;
    }
    if (item.productType !== productType) {
      return false;
    }
    if (!item.metadata) {
      return true;
    }
    return (
      item.metadata.productId === productId &&
      item.metadata.tradingSystem === tradingSystem
    );
  });
};

const isUserCouponRedeemAble = (coupon: UserCoupon) => {
  const item = coupon.items[0];
  return (
    item.productType === ProductType.Program ||
    item.productType === ProductType.General
  );
};

export {
  createDisplayNotificationDate,
  logMobxData,
  getClientDevice,
  blobToBase64,
  downloadCsv,
  getTotalPayoutImageTag,
  filterAchievementsByType,
  calculateTotalPayout,
  getContestBreakpoints,
  contestPlaceSuffix,
  filterCouponsForProduct,
  isUserCouponRedeemAble,
};

export * from './translation';
export * from './date';
export * from './currency';
export * from './validations';
export * from './phone-number';
export * from './can-resend-invite';
export * from './contest';
export * from './string-avatar';
export * from './get-ordinal';
export * from './local-currency-code';
export * from './asset';
export * from './payment-provider';
export * from './prize-display-converter';
export * from './url';
export * from './user';
